<template>

   <!-- XOL-392 (2026-08-20): `inline` distinguishes the two layouts. Register
        keeps the original right-aligned 174px labels; the stacked consumers
        (PasswordDialog, and ContactDetail's create-time credential) move to
        top labels, which is this repo's dominant stacked-form convention and
        what closes the dead gap between label and field. `label-width` is left
        bound on both items deliberately: Element ignores it entirely when
        label-position is "top" (form-item.vue labelStyle/contentStyle both
        return {}), so one binding serves both variants. -->
   <el-form class="text-left"
            :class="{ 'password-form--stacked': !inline }"
            :inline="inline"
            :label-position="inline ? 'right' : 'top'"
            :model="form">

         <el-form-item label="New Password" for="new-password" :label-width="formLabelWidth">
          <password id="new-password" v-model="form.password" :toggle="toggle"
            autocomplete="new-password" :secure-length="8"
            aria-describedby="password-hint password-rules" />

          <!-- XOL-392: the length rule is a permanent hint, never an alert.
               It is on screen the moment the dialog opens, which is what makes
               a disabled Save acceptable, and it stops "must be at least 8
               characters" firing as an ERROR at character 1 of a perfectly
               good password. Live count is carried by the library's own
               in-field badge. -->
          <small id="password-hint" class="text-muted password-form__hint">At least 8 characters.</small>
        </el-form-item>

        <el-form-item label="Confirm New Password" for="confirm-password" :label-width="formLabelWidth">
          <password id="confirm-password" v-model="form.c_password" :toggle="toggle"
            autocomplete="new-password" :secure-length="8" aria-describedby="password-rules"
            @blur="$emit('confirm-blur')" />
        </el-form-item>

     </el-form>

</template>

<script>

import PostMixins from '@/mixins/PostMixins'

import Password from 'vue-password-strength-meter'


export default {

  mixins: [PostMixins],

  components: {
    Password
  },

  props: {
    value: {
      required: true
    },

    inline: {
      type: Boolean,
      default: true
    },

    change: {
      type: Boolean,
      default: false
    },

    newPsw: {
      type: Boolean,
      default: false
    },

    toggle: {
      type: Boolean,
      default: true
    }

  },



  watch: {

    value: function (d) {
      this.form = d
    }

  },




  // XOL-392: changePassword() and resetPassword() were removed here — neither
  // had a caller (this template has never had buttons), and resetPassword()
  // raised an alert about a control this ticket deleted. The dialog's own
  // methods are the live ones.

  data () {
    return {
      form: this.value,
      formLabelWidth: '174px'
    }
  },

  created () {
    // XOL-392: PasswordForm is now mounted standalone on create (inline, no
    // PasswordDialog wrapper). The contacts struct has a `password` key but
    // no `c_password` — it is not a DB column — so v-model="form.c_password"
    // would write a NON-reactive property. Mirrors PasswordDialog.vue's own
    // created()-time seeding (XOL-391) so this component is self-sufficient;
    // the double-seed (PasswordDialog also seeds the same shared object) is
    // idempotent.
    if (this.form) {
      if (!Object.prototype.hasOwnProperty.call(this.form, 'password')) {
        this.$set(this.form, 'password', null)
      }
      if (!Object.prototype.hasOwnProperty.call(this.form, 'c_password')) {
        this.$set(this.form, 'c_password', null)
      }
    }
  }

}

</script>

<!--
  XOL-392 (2026-08-20): a style block is unavoidable here. Everything overridden
  below is GLOBAL css injected by vue-password-strength-meter's own bundle, and
  the package exposes no prop for any of it: the #f1f1f1 input fill that made
  the field read as disabled, the four white notches painted over the meter
  track (which is why an empty field showed "five disconnected grey segments"),
  and the darkred/orangered/yellowgreen score colours that read as foreign
  chrome next to Element's palette. `showStrengthMeter` is all-or-nothing and
  controls none of it. ::v-deep is the established idiom in this repo
  (ExpiryTable, Tenancy, ContractorInvoiceTable, ~a dozen more).

  The input and meter rules are deliberately NOT scoped to --stacked: they
  reach Register too, where the password pair was the only grey field among
  white el-inputs. That is an improvement, and it is the contrast case the PR
  screenshots must show.
-->
<style lang="scss" scoped>

/* Match el-input__inner exactly. 84px of right padding reserves the in-field
   icon stack (count badge + reveal toggle, both position:absolute right:0) so
   typed text can never slide underneath it. */
::v-deep .Password__field {
  height: 40px;
  line-height: 40px;
  padding: 0 84px 0 15px;
  background-color: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #606266;
  transition: border-color .2s cubic-bezier(.645, .045, .355, 1);
}

::v-deep .Password__field:focus {
  outline: none;
  border-color: #409eff;
}

/* One continuous track. The :before/:after are the library's white notches. */
::v-deep .Password__strength-meter {
  height: 4px;
  background: #ebeef5;
  margin: 8px 0 0;
  border-radius: 2px;
}

::v-deep .Password__strength-meter:before,
::v-deep .Password__strength-meter:after {
  display: none;
}

/* Element danger / warning / success, replacing the library's own palette. */
::v-deep .Password__strength-meter--fill[data-score="0"],
::v-deep .Password__strength-meter--fill[data-score="1"] { background: #f56c6c; }
::v-deep .Password__strength-meter--fill[data-score="2"] { background: #e6a23c; }
::v-deep .Password__strength-meter--fill[data-score="3"],
::v-deep .Password__strength-meter--fill[data-score="4"] { background: #67c23a; }

.password-form__hint {
  display: block;
  margin-top: 6px;
}

/* Stacked only — Register keeps the library's centring and Element's spacing. */
.password-form--stacked {

  /* The library centres itself with `margin: 0 auto` inside a wider column,
     which put the input's left edge ~53px right of the label. The 400px cap it
     also sets is KEPT on purpose: the dialog's content column is exactly 400px,
     and on ContactDetail's full-width create column it stops the field
     stretching absurdly. */
  ::v-deep .Password {
    margin: 0;
  }

  /* Belt-and-braces against the XOL-451 descendant leak, which has now bitten
     this component three times. `.el-form--inline .el-form-item` is a
     descendant selector, so ANY inline-form ancestor — however far up —
     collapses these fields to shrink-to-fit inline-blocks regardless of this
     form's own :inline="false". The dialog escapes that by rendering to <body>
     (append-to-body) and the create-time block by being a sibling rather than a
     descendant, but both are positional guarantees that a future refactor can
     silently undo. This one is not positional: the stacked variant stays
     stacked wherever it is mounted. */
  ::v-deep .el-form-item {
    display: block;
    margin-right: 0;
    margin-bottom: 16px;
  }
}

</style>
